// useSideMenu.js
// Custom hooks that handle data fetching and business logic for SideMenuNew.

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Modal } from "antd";
import LoginService from "../../services/LoginService";
import { clearApiClient } from "../../services/apiClient";

// ---------------------------------------------------------------------------
// Config settings
// ---------------------------------------------------------------------------

const CONFIG_FIELDS = [
  "STUDENT_CAN_CHANGE_USERNAME",
  "STUDENT_CAN_CHANGE_PASSWORD",
  "HAS_ONLINE_APPLICATION",
  "HAS_ZOOM",
  "HAS_EMERGENCY_SHEET",
  "HAS_BRAINEE_CONFERENCE",
  "HAS_MICROCREDENTIALS",
];

const BACKGROUND_REQUEST_TIMEOUT_MS = 10000;
const DEFAULT_CONFIG = {
  userCanChangeUsername: true,
  userCanChangePassword: true,
  hasOA: false,
  hasZoom: false,
  hasEmergencySheet: false,
  hasBraineeConference: false,
  hasMicrocredentials: false,
};

let configSettingsCache = null;
let configSettingsRequest = null;
let currentSchoolYearCache = null;
let currentSchoolYearRequest = null;

function loadConfigSettings() {
  if (configSettingsCache) return Promise.resolve(configSettingsCache);
  if (configSettingsRequest) return configSettingsRequest;

  configSettingsRequest = Promise.all(
    CONFIG_FIELDS.map((field) =>
      axios
        .get(`${process.env.REACT_APP_API_MANAGEMENT}/configsettingvalue?cfield=${field}`, {
          timeout: BACKGROUND_REQUEST_TIMEOUT_MS,
        })
        .then((res) => ({ field, value: res.data }))
        .catch(() => null)
    )
  )
    .then((results) => {
      const nextConfig = { ...DEFAULT_CONFIG };
      let hasResult = false;

      results.forEach((result) => {
        if (!result) return;
        hasResult = true;
        const isYes = result.value === "yes";
        switch (result.field) {
          case "STUDENT_CAN_CHANGE_USERNAME": nextConfig.userCanChangeUsername = isYes; break;
          case "STUDENT_CAN_CHANGE_PASSWORD": nextConfig.userCanChangePassword = isYes; break;
          case "HAS_ONLINE_APPLICATION": nextConfig.hasOA = isYes; break;
          case "HAS_ZOOM": nextConfig.hasZoom = isYes; break;
          case "HAS_EMERGENCY_SHEET": nextConfig.hasEmergencySheet = isYes; break;
          case "HAS_BRAINEE_CONFERENCE": nextConfig.hasBraineeConference = isYes; break;
          case "HAS_MICROCREDENTIALS": nextConfig.hasMicrocredentials = isYes; break;
          default: break;
        }
      });

      if (hasResult) configSettingsCache = nextConfig;
      return nextConfig;
    })
    .finally(() => {
      configSettingsRequest = null;
    });

  return configSettingsRequest;
}

function loadCurrentSchoolYear() {
  if (currentSchoolYearCache !== null) return Promise.resolve(currentSchoolYearCache);
  if (currentSchoolYearRequest) return currentSchoolYearRequest;

  currentSchoolYearRequest = axios
    .get(`${process.env.REACT_APP_API_MANAGEMENT}/currentschoolyear`, {
      timeout: BACKGROUND_REQUEST_TIMEOUT_MS,
    })
    .then((res) => {
      const schoolYear = res.data || null;
      if (schoolYear) currentSchoolYearCache = schoolYear;
      return schoolYear;
    })
    .catch((err) => {
      console.error(err.message);
      return null;
    })
    .finally(() => {
      currentSchoolYearRequest = null;
    });

  return currentSchoolYearRequest;
}

/**
 * Fetches all relevant config flags in parallel.
 * Returns { userCanChangeUsername, userCanChangePassword, hasOA, hasZoom, hasEmergencySheet }.
 */
export function useConfigSettings() {
  const [config, setConfig] = useState(() => configSettingsCache || DEFAULT_CONFIG);

  useEffect(() => {
    let isCurrent = true;
    loadConfigSettings().then((nextConfig) => {
      if (isCurrent) setConfig(nextConfig);
    });
    return () => { isCurrent = false; };
  }, []);

  return config;
}

// ---------------------------------------------------------------------------
// Current school year
// ---------------------------------------------------------------------------

export function useCurrentSchoolYear() {
  const [currentSchoolYear, setCurrentSchoolYearState] = useState(currentSchoolYearCache);

  const setCurrentSchoolYear = useCallback((value) => {
    setCurrentSchoolYearState((previousValue) => {
      const nextValue = typeof value === "function" ? value(previousValue) : value;
      currentSchoolYearCache = nextValue;
      return nextValue;
    });
  }, []);

  useEffect(() => {
    let isCurrent = true;
    loadCurrentSchoolYear().then((schoolYear) => {
      if (isCurrent && schoolYear) setCurrentSchoolYearState(schoolYear);
    });
    return () => { isCurrent = false; };
  }, []);

  return { currentSchoolYear, setCurrentSchoolYear };
}

// ---------------------------------------------------------------------------
// School year change modal
// ---------------------------------------------------------------------------

export function useSchoolYearModal(currentSchoolYear, setCurrentSchoolYear) {
  const [visible, setVisible] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [schoolYear, setSchoolYear] = useState(null);
  const [schoolYearList, setSchoolYearList] = useState([]);

  const open = () => {
    axios
      .get(`${process.env.REACT_APP_API_LOOKUP}/schoolyearlist`)
      .then((res) => {
        if (res.data) {
          setSchoolYearList(res.data.map(({ value, name }) => ({ value, name })));
          setVisible(true);
        }
      })
      .catch((err) => console.error(err.message));
  };

  const close = () => setVisible(false);

  const confirm = () => {
    setUpdating(true);
    axios
      .post(`${process.env.REACT_APP_API_MANAGEMENT}/changeschoolyear?schoolyear=${schoolYear}`)
      .then((res) => {
        setUpdating(false);
        if (res.data) {
          setCurrentSchoolYear(schoolYear);
          Modal.success({
            title: "Change School Year",
            content: "Changing school year successful!",
            centered: true,
            onOk: close,
          });
        }
      })
      .catch((err) => {
        setUpdating(false);
        Modal.error({
          title: "Change School Year",
          content: err.response?.data ?? "An error occurred.",
          centered: true,
        });
      });
  };

  return {
    visible,
    updating,
    schoolYear,
    schoolYearList,
    currentSchoolYear,
    open,
    close,
    confirm,
    setSchoolYear,
  };
}

// ---------------------------------------------------------------------------
// Logout
// ---------------------------------------------------------------------------

export function useLogout(username) {
  const { confirm } = Modal;

  return () => {
    confirm({
      centered: true,
      zIndex: 2000,
      title: "Are you sure you want to logout?",
      onOk() {
        // Clear the session locally first. Logging out must never depend on the
        // network, otherwise a failed, timed out or aborted request (common on
        // mobile data) leaves the user still logged in with no error shown.
        localStorage.removeItem("lms-jwt");
        // Drops the bearer token from the axios defaults it was installed on, through the
        // module that installed it (services/apiClient.js) - a screen never writes
        // axios.defaults itself, so the two cannot disagree about which headers exist.
        clearApiClient();

        // Best-effort server notification. Failures are intentionally ignored -
        // the logout API is a no-op, so it must never block the local logout.
        LoginService.logout(username).catch(() => {});

        // replace() instead of href so the Android Back button/gesture cannot
        // return to the still-rendered logged-in page.
        window.location.replace("/");
      },
    });
  };
}

// ---------------------------------------------------------------------------
// E-Library link resolver
// ---------------------------------------------------------------------------

const E_LIBRARY_URLS = {
  lcccaloocan:       "https://lcccaloocan-library.braineeph.com",
  smapasay:          "https://smapasay-library.braineeph.com",
  uvca:              "https://uvca-library.braineeph.com",
  mcamandaluyong:    "https://mcamandaluyong-library.braineeph.com",
  sanlorenzoschoolph:"https://sanlorenzoschoolph-library.braineeph.com",
  fiatluxacademe:    "https://flagentri-library.braineeph.com",
  "dba-bacolor":     "https://dba-bacolor-library.braineeph.com",
  sdca:              "https://sdca-library.braineeph.com",
  hia:               "https://hia-library.braineeph.com",
  pga:               "https://pga-library.braineeph.com",
  gsa:               "https://gsa-library.braineeph.com",
  ihma:              "https://ihma-library.braineeph.com",
  sha:               "https://sha-library.braineeph.com",
  sja:               "https://sja-library.braineeph.com",
  snsr:              "https://snsr-library.braineeph.com",
  mchs:              "https://mchs-library.braineeph.com",
};

export function useELibraryLink(subdomain, onUnavailable) {
  return () => {
    const url = E_LIBRARY_URLS[subdomain];
    if (url) window.open(url, "_blank").focus();
    else onUnavailable();
  };
}
